import type { Idea, User, Comment } from "./types";

const STORAGE_KEYS = {
	ideas: "sia_ideas",
	users: "sia_users",
	comments: "sia_comments",
	currentUser: "sia_current_user",
} as const;

function generateId(): string {
	return Date.now().toString(36) + Math.random().toString(36).substring(2, 9);
}

function load<T>(key: string, fallback: T): T {
	try {
		const data = localStorage.getItem(key);
		return data ? JSON.parse(data) : fallback;
	} catch {
		return fallback;
	}
}

function save<T>(key: string, data: T): void {
	localStorage.setItem(key, JSON.stringify(data));
}

// Seed data
const SEED_USERS: User[] = [
	{
		id: "user_1",
		name: "Sarah Chen",
		avatar: "SC",
		reputation: 342,
		ideasSubmitted: 12,
		totalVotesReceived: 287,
		joinedAt: "2025-11-15T10:00:00Z",
	},
	{
		id: "user_2",
		name: "Marcus Johnson",
		avatar: "MJ",
		reputation: 218,
		ideasSubmitted: 8,
		totalVotesReceived: 164,
		joinedAt: "2025-12-01T10:00:00Z",
	},
	{
		id: "user_3",
		name: "Priya Patel",
		avatar: "PP",
		reputation: 189,
		ideasSubmitted: 6,
		totalVotesReceived: 145,
		joinedAt: "2026-01-05T10:00:00Z",
	},
	{
		id: "user_4",
		name: "Alex Rivera",
		avatar: "AR",
		reputation: 156,
		ideasSubmitted: 5,
		totalVotesReceived: 112,
		joinedAt: "2026-01-10T10:00:00Z",
	},
	{
		id: "user_5",
		name: "Emma Wilson",
		avatar: "EW",
		reputation: 97,
		ideasSubmitted: 3,
		totalVotesReceived: 78,
		joinedAt: "2026-01-20T10:00:00Z",
	},
];

const SEED_IDEAS: Idea[] = [
	{
		id: "idea_1",
		title: "MealMind - AI Meal Planning for Dietary Restrictions",
		problem:
			"People with multiple dietary restrictions (allergies, diabetes, religious) spend hours weekly planning meals. Existing apps only handle one restriction at a time and ignore cultural food preferences.",
		solution:
			"An AI-powered meal planner that layers multiple dietary constraints simultaneously. Uses a knowledge graph of ingredients, nutritional data, and cultural cuisine patterns to generate weekly meal plans with grocery lists. Learns preferences over time.",
		targetMarket:
			"30M+ Americans with food allergies, 37M diabetics, and millions following religious dietary laws. Primary: health-conscious families managing multiple restrictions. Secondary: nutritionists and dietitians seeking tools for clients.",
		monetization:
			"Freemium model. Free tier: basic single-restriction planning. Pro ($9.99/mo): multi-restriction, family plans, grocery delivery integration. B2B tier for healthcare providers at $49/seat/mo. Affiliate revenue from grocery partners.",
		tags: ["AI/ML", "Healthcare", "B2C"],
		authorId: "user_1",
		forkedFromId: null,
		createdAt: "2026-02-10T14:30:00Z",
		updatedAt: "2026-02-10T14:30:00Z",
		votes: 47,
		votedBy: ["user_2", "user_3", "user_4", "user_5"],
		commentCount: 12,
		versions: [
			{
				id: "v_1_1",
				ideaId: "idea_1",
				version: 1,
				problem:
					"People with multiple dietary restrictions (allergies, diabetes, religious) spend hours weekly planning meals. Existing apps only handle one restriction at a time and ignore cultural food preferences.",
				solution:
					"An AI-powered meal planner that layers multiple dietary constraints simultaneously. Uses a knowledge graph of ingredients, nutritional data, and cultural cuisine patterns to generate weekly meal plans with grocery lists. Learns preferences over time.",
				targetMarket:
					"30M+ Americans with food allergies, 37M diabetics, and millions following religious dietary laws. Primary: health-conscious families managing multiple restrictions. Secondary: nutritionists and dietitians seeking tools for clients.",
				monetization:
					"Freemium model. Free tier: basic single-restriction planning. Pro ($9.99/mo): multi-restriction, family plans, grocery delivery integration. B2B tier for healthcare providers at $49/seat/mo. Affiliate revenue from grocery partners.",
				createdAt: "2026-02-10T14:30:00Z",
				authorId: "user_1",
				changeNote: "Initial submission",
			},
		],
		currentVersion: 1,
	},
	{
		id: "idea_2",
		title: "CodeReview.ai - Automated PR Reviews for Small Teams",
		problem:
			"Small dev teams (2-10 engineers) lack senior reviewers. PRs sit for days waiting for review, shipping velocity drops, and bugs slip through. Hiring senior devs just for reviews is cost-prohibitive.",
		solution:
			"An AI code review bot that plugs into GitHub/GitLab. Goes beyond linting — understands project context, architectural patterns, and team conventions. Provides actionable, educational feedback ranked by severity. Learns from team's merge decisions.",
		targetMarket:
			"2.5M+ small development teams globally. Primary: seed-to-Series A startups with 2-10 engineers. Secondary: bootcamp graduates and solo developers seeking mentorship-quality feedback on their code.",
		monetization:
			"Usage-based pricing. Free: 50 PR reviews/month. Team ($29/mo): 500 reviews, priority processing. Enterprise ($99/mo): unlimited reviews, custom rules, SOC2 compliance. Upsell: team analytics dashboard at $19/mo add-on.",
		tags: ["DevTools", "AI/ML", "SaaS", "B2B"],
		authorId: "user_2",
		forkedFromId: null,
		createdAt: "2026-02-09T09:15:00Z",
		updatedAt: "2026-02-11T16:00:00Z",
		votes: 83,
		votedBy: ["user_1", "user_3", "user_4", "user_5"],
		commentCount: 24,
		versions: [
			{
				id: "v_2_1",
				ideaId: "idea_2",
				version: 1,
				problem:
					"Small dev teams lack code review processes. PRs sit unreviewed for days.",
				solution:
					"An AI code review bot for GitHub that provides automated feedback on PRs.",
				targetMarket: "Small development teams and startups.",
				monetization: "SaaS subscription model at $29/mo per team.",
				createdAt: "2026-02-09T09:15:00Z",
				authorId: "user_2",
				changeNote: "Initial submission",
			},
			{
				id: "v_2_2",
				ideaId: "idea_2",
				version: 2,
				problem:
					"Small dev teams (2-10 engineers) lack senior reviewers. PRs sit for days waiting for review, shipping velocity drops, and bugs slip through. Hiring senior devs just for reviews is cost-prohibitive.",
				solution:
					"An AI code review bot that plugs into GitHub/GitLab. Goes beyond linting — understands project context, architectural patterns, and team conventions. Provides actionable, educational feedback ranked by severity. Learns from team's merge decisions.",
				targetMarket:
					"2.5M+ small development teams globally. Primary: seed-to-Series A startups with 2-10 engineers. Secondary: bootcamp graduates and solo developers seeking mentorship-quality feedback on their code.",
				monetization:
					"Usage-based pricing. Free: 50 PR reviews/month. Team ($29/mo): 500 reviews, priority processing. Enterprise ($99/mo): unlimited reviews, custom rules, SOC2 compliance. Upsell: team analytics dashboard at $19/mo add-on.",
				createdAt: "2026-02-11T16:00:00Z",
				authorId: "user_2",
				changeNote: "Expanded market analysis and pricing tiers",
			},
		],
		currentVersion: 2,
	},
	{
		id: "idea_3",
		title: "GreenLedger - Carbon Footprint Tracking for SMBs",
		problem:
			"Small and medium businesses face growing regulatory pressure to report carbon emissions but lack affordable tools. Enterprise solutions cost $50K+/year. Most SMBs resort to error-prone spreadsheets or ignore reporting entirely.",
		solution:
			"Automated carbon tracking that integrates with accounting software (QuickBooks, Xero). Calculates Scope 1-3 emissions from financial transactions using industry emission factors. Generates compliance-ready reports for ESG frameworks.",
		targetMarket:
			"33M+ SMBs in the US alone, with EU regulations requiring climate reporting from companies with 250+ employees by 2026. Primary: SMBs in supply chains of large corporations requiring Scope 3 data from vendors.",
		monetization:
			"Tiered SaaS. Starter ($19/mo): basic tracking, annual report. Growth ($59/mo): multi-entity, API access, quarterly reports. Pro ($149/mo): audit-ready reports, consultant marketplace. Carbon offset marketplace with 5% commission.",
		tags: ["Climate", "SaaS", "B2B", "Fintech"],
		authorId: "user_3",
		forkedFromId: null,
		createdAt: "2026-02-08T11:45:00Z",
		updatedAt: "2026-02-08T11:45:00Z",
		votes: 62,
		votedBy: ["user_1", "user_2", "user_4"],
		commentCount: 18,
		versions: [
			{
				id: "v_3_1",
				ideaId: "idea_3",
				version: 1,
				problem:
					"Small and medium businesses face growing regulatory pressure to report carbon emissions but lack affordable tools. Enterprise solutions cost $50K+/year. Most SMBs resort to error-prone spreadsheets or ignore reporting entirely.",
				solution:
					"Automated carbon tracking that integrates with accounting software (QuickBooks, Xero). Calculates Scope 1-3 emissions from financial transactions using industry emission factors. Generates compliance-ready reports for ESG frameworks.",
				targetMarket:
					"33M+ SMBs in the US alone, with EU regulations requiring climate reporting from companies with 250+ employees by 2026. Primary: SMBs in supply chains of large corporations requiring Scope 3 data from vendors.",
				monetization:
					"Tiered SaaS. Starter ($19/mo): basic tracking, annual report. Growth ($59/mo): multi-entity, API access, quarterly reports. Pro ($149/mo): audit-ready reports, consultant marketplace. Carbon offset marketplace with 5% commission.",
				createdAt: "2026-02-08T11:45:00Z",
				authorId: "user_3",
				changeNote: "Initial submission",
			},
		],
		currentVersion: 1,
	},
	{
		id: "idea_4",
		title: "SkillSwap - Peer-to-Peer Micro-Learning Marketplace",
		problem:
			"Professionals want to learn niche skills (e.g., Figma prototyping, SQL optimization) but courses are too long and expensive. Meanwhile, experts in these areas have no easy way to monetize bite-sized knowledge.",
		solution:
			"A marketplace for 15-30 minute live micro-sessions. Experts set availability and topics, learners book and pay per session. Built-in video, screen sharing, and collaborative workspace. AI matches learners to the best available experts.",
		targetMarket:
			"Target: 500M+ knowledge workers globally seeking continuous upskilling. Initial focus on tech professionals. TAM based on $370B corporate training market and growing freelance economy.",
		monetization:
			"20% platform commission on sessions ($5-50 each). Premium subscriptions ($14.99/mo) for unlimited sessions from a curated pool. Enterprise packages for team learning budgets. Expert certification program ($99 one-time).",
		tags: ["EdTech", "Marketplace", "B2C", "B2B"],
		authorId: "user_4",
		forkedFromId: null,
		createdAt: "2026-02-07T16:20:00Z",
		updatedAt: "2026-02-07T16:20:00Z",
		votes: 41,
		votedBy: ["user_1", "user_5"],
		commentCount: 9,
		versions: [
			{
				id: "v_4_1",
				ideaId: "idea_4",
				version: 1,
				problem:
					"Professionals want to learn niche skills (e.g., Figma prototyping, SQL optimization) but courses are too long and expensive. Meanwhile, experts in these areas have no easy way to monetize bite-sized knowledge.",
				solution:
					"A marketplace for 15-30 minute live micro-sessions. Experts set availability and topics, learners book and pay per session. Built-in video, screen sharing, and collaborative workspace. AI matches learners to the best available experts.",
				targetMarket:
					"Target: 500M+ knowledge workers globally seeking continuous upskilling. Initial focus on tech professionals. TAM based on $370B corporate training market and growing freelance economy.",
				monetization:
					"20% platform commission on sessions ($5-50 each). Premium subscriptions ($14.99/mo) for unlimited sessions from a curated pool. Enterprise packages for team learning budgets. Expert certification program ($99 one-time).",
				createdAt: "2026-02-07T16:20:00Z",
				authorId: "user_4",
				changeNote: "Initial submission",
			},
		],
		currentVersion: 1,
	},
	{
		id: "idea_5",
		title: "RentGuard - Tenant Rights Protection Platform",
		problem:
			"Tenants face illegal rent increases, withheld security deposits, and uninhabitable conditions but don't know their rights or can't afford legal help. Legal aid is overwhelmed and most tenant rights info online is outdated or jurisdiction-wrong.",
		solution:
			"An app that uses your lease and location to provide personalized legal guidance. Scans lease for problematic clauses, tracks landlord compliance, auto-generates legally valid complaint letters, and connects users to pro-bono attorneys when needed.",
		targetMarket:
			"44M+ renter households in the US. Primary: low-to-middle income renters in major metro areas. Secondary: tenant advocacy organizations and legal aid nonprofits seeking scalable outreach tools.",
		monetization:
			"Freemium. Free: lease scanner, basic rights info. Pro ($4.99/mo): complaint letter generator, landlord communication tracking, priority attorney matching. B2B: white-label for legal aid orgs ($500/mo). Referral fees from attorney network.",
		tags: ["Real Estate", "B2C", "Social"],
		authorId: "user_5",
		forkedFromId: null,
		createdAt: "2026-02-06T08:00:00Z",
		updatedAt: "2026-02-06T08:00:00Z",
		votes: 55,
		votedBy: ["user_1", "user_2", "user_3"],
		commentCount: 15,
		versions: [
			{
				id: "v_5_1",
				ideaId: "idea_5",
				version: 1,
				problem:
					"Tenants face illegal rent increases, withheld security deposits, and uninhabitable conditions but don't know their rights or can't afford legal help. Legal aid is overwhelmed and most tenant rights info online is outdated or jurisdiction-wrong.",
				solution:
					"An app that uses your lease and location to provide personalized legal guidance. Scans lease for problematic clauses, tracks landlord compliance, auto-generates legally valid complaint letters, and connects users to pro-bono attorneys when needed.",
				targetMarket:
					"44M+ renter households in the US. Primary: low-to-middle income renters in major metro areas. Secondary: tenant advocacy organizations and legal aid nonprofits seeking scalable outreach tools.",
				monetization:
					"Freemium. Free: lease scanner, basic rights info. Pro ($4.99/mo): complaint letter generator, landlord communication tracking, priority attorney matching. B2B: white-label for legal aid orgs ($500/mo). Referral fees from attorney network.",
				createdAt: "2026-02-06T08:00:00Z",
				authorId: "user_5",
				changeNote: "Initial submission",
			},
		],
		currentVersion: 1,
	},
	{
		id: "idea_6",
		title: "MealMind Pro - AI Meal Planning with Social Features",
		problem:
			"People with dietary restrictions struggle with meal planning, and existing solutions are isolating. There's no community aspect to share adapted recipes or get support from others with similar restrictions.",
		solution:
			"Building on MealMind's core AI planning, adding a social layer: share adapted recipes, follow users with similar restrictions, community challenges, and a 'restriction twin' matching system for peer support and recipe exchange.",
		targetMarket:
			"Same as MealMind (30M+ with allergies, 37M diabetics) but adding the 15M+ Americans following specialty diets (keto, vegan) who are active in online food communities and willing to pay for social features.",
		monetization:
			"Community freemium. Free: basic planning + community read. Social ($6.99/mo): post recipes, follow users, join challenges. Pro ($12.99/mo): full AI planning + social. Creator program: top recipe creators earn 70% of tip revenue.",
		tags: ["AI/ML", "Healthcare", "Social", "B2C"],
		authorId: "user_3",
		forkedFromId: "idea_1",
		createdAt: "2026-02-12T10:00:00Z",
		updatedAt: "2026-02-12T10:00:00Z",
		votes: 29,
		votedBy: ["user_1", "user_4"],
		commentCount: 7,
		versions: [
			{
				id: "v_6_1",
				ideaId: "idea_6",
				version: 1,
				problem:
					"People with dietary restrictions struggle with meal planning, and existing solutions are isolating. There's no community aspect to share adapted recipes or get support from others with similar restrictions.",
				solution:
					"Building on MealMind's core AI planning, adding a social layer: share adapted recipes, follow users with similar restrictions, community challenges, and a 'restriction twin' matching system for peer support and recipe exchange.",
				targetMarket:
					"Same as MealMind (30M+ with allergies, 37M diabetics) but adding the 15M+ Americans following specialty diets (keto, vegan) who are active in online food communities and willing to pay for social features.",
				monetization:
					"Community freemium. Free: basic planning + community read. Social ($6.99/mo): post recipes, follow users, join challenges. Pro ($12.99/mo): full AI planning + social. Creator program: top recipe creators earn 70% of tip revenue.",
				createdAt: "2026-02-12T10:00:00Z",
				authorId: "user_3",
				changeNote: "Forked from MealMind - added social features",
			},
		],
		currentVersion: 1,
	},
];

const SEED_COMMENTS: Comment[] = [
	{
		id: "c_1",
		ideaId: "idea_2",
		authorId: "user_1",
		content:
			"This is really compelling. The educational feedback angle differentiates it from existing linters. Have you thought about integrating with IDE extensions too?",
		createdAt: "2026-02-09T12:00:00Z",
	},
	{
		id: "c_2",
		ideaId: "idea_2",
		authorId: "user_3",
		content:
			"Love the learning-from-merge-decisions feature. That's a real moat. Pricing might be too low for enterprise though — they'd pay 10x for SOC2 compliance.",
		createdAt: "2026-02-09T14:30:00Z",
	},
	{
		id: "c_3",
		ideaId: "idea_1",
		authorId: "user_2",
		content:
			"The knowledge graph approach is smart. I'd add a meal prep / batch cooking mode for busy families. That's the real pain point.",
		createdAt: "2026-02-10T16:00:00Z",
	},
	{
		id: "c_4",
		ideaId: "idea_3",
		authorId: "user_1",
		content:
			"Timing is perfect with EU CSRD regulations. The accounting software integration is the key insight — that's where the data already lives.",
		createdAt: "2026-02-08T15:00:00Z",
	},
	{
		id: "c_5",
		ideaId: "idea_5",
		authorId: "user_3",
		content:
			"This could be huge. The legal letter generator alone would save tenants thousands. Consider partnering with tenant unions for distribution.",
		createdAt: "2026-02-06T12:00:00Z",
	},
];

function initializeStore(): {
	ideas: Idea[];
	users: User[];
	comments: Comment[];
} {
	const existingIdeas = load<Idea[]>(STORAGE_KEYS.ideas, []);
	if (existingIdeas.length > 0) {
		return {
			ideas: existingIdeas,
			users: load<User[]>(STORAGE_KEYS.users, SEED_USERS),
			comments: load<Comment[]>(STORAGE_KEYS.comments, SEED_COMMENTS),
		};
	}

	save(STORAGE_KEYS.ideas, SEED_IDEAS);
	save(STORAGE_KEYS.users, SEED_USERS);
	save(STORAGE_KEYS.comments, SEED_COMMENTS);
	return {
		ideas: SEED_IDEAS,
		users: SEED_USERS,
		comments: SEED_COMMENTS,
	};
}

let store = initializeStore();

function getCurrentUser(): User {
	const stored = load<string | null>(STORAGE_KEYS.currentUser, null);
	if (stored) {
		const user = store.users.find((u) => u.id === stored);
		if (user) return user;
	}
	// Default to creating a new user for the visitor
	const existing = store.users.find((u) => u.id === "user_me");
	if (existing) return existing;

	const me: User = {
		id: "user_me",
		name: "You",
		avatar: "YO",
		reputation: 10,
		ideasSubmitted: 0,
		totalVotesReceived: 0,
		joinedAt: new Date().toISOString(),
	};
	store.users.push(me);
	save(STORAGE_KEYS.users, store.users);
	save(STORAGE_KEYS.currentUser, me.id);
	return me;
}

export function getStore() {
	return { ...store, currentUser: getCurrentUser() };
}

export function getUser(id: string): User | undefined {
	return store.users.find((u) => u.id === id);
}

export function getIdea(id: string): Idea | undefined {
	return store.ideas.find((i) => i.id === id);
}

export function getIdeas(): Idea[] {
	return [...store.ideas];
}

export function getComments(ideaId: string): Comment[] {
	return store.comments.filter((c) => c.ideaId === ideaId);
}

export function addIdea(
	idea: Omit<
		Idea,
		| "id"
		| "createdAt"
		| "updatedAt"
		| "votes"
		| "votedBy"
		| "commentCount"
		| "versions"
		| "currentVersion"
	>,
): Idea {
	const id = "idea_" + generateId();
	const now = new Date().toISOString();
	const newIdea: Idea = {
		...idea,
		id,
		createdAt: now,
		updatedAt: now,
		votes: 0,
		votedBy: [],
		commentCount: 0,
		versions: [
			{
				id: "v_" + generateId(),
				ideaId: id,
				version: 1,
				problem: idea.problem,
				solution: idea.solution,
				targetMarket: idea.targetMarket,
				monetization: idea.monetization,
				createdAt: now,
				authorId: idea.authorId,
				changeNote: idea.forkedFromId
					? "Forked and improved"
					: "Initial submission",
			},
		],
		currentVersion: 1,
	};
	store.ideas.unshift(newIdea);
	save(STORAGE_KEYS.ideas, store.ideas);

	// Update user stats
	const user = store.users.find((u) => u.id === idea.authorId);
	if (user) {
		user.ideasSubmitted++;
		user.reputation += 5;
		save(STORAGE_KEYS.users, store.users);
	}

	return newIdea;
}

export function updateIdea(
	ideaId: string,
	updates: {
		problem: string;
		solution: string;
		targetMarket: string;
		monetization: string;
		title: string;
		tags: string[];
		changeNote: string;
	},
	authorId: string,
): Idea | null {
	const idea = store.ideas.find((i) => i.id === ideaId);
	if (!idea) return null;

	const now = new Date().toISOString();
	const newVersion = idea.currentVersion + 1;
	idea.problem = updates.problem;
	idea.solution = updates.solution;
	idea.targetMarket = updates.targetMarket;
	idea.monetization = updates.monetization;
	idea.title = updates.title;
	idea.tags = updates.tags;
	idea.updatedAt = now;
	idea.currentVersion = newVersion;
	idea.versions.push({
		id: "v_" + generateId(),
		ideaId,
		version: newVersion,
		problem: updates.problem,
		solution: updates.solution,
		targetMarket: updates.targetMarket,
		monetization: updates.monetization,
		createdAt: now,
		authorId,
		changeNote: updates.changeNote,
	});

	save(STORAGE_KEYS.ideas, store.ideas);
	return idea;
}

export function voteIdea(ideaId: string, userId: string): Idea | null {
	const idea = store.ideas.find((i) => i.id === ideaId);
	if (!idea) return null;

	const hasVoted = idea.votedBy.includes(userId);
	if (hasVoted) {
		idea.votedBy = idea.votedBy.filter((id) => id !== userId);
		idea.votes--;
		const author = store.users.find((u) => u.id === idea.authorId);
		if (author) {
			author.totalVotesReceived--;
			author.reputation = Math.max(0, author.reputation - 1);
		}
	} else {
		idea.votedBy.push(userId);
		idea.votes++;
		const author = store.users.find((u) => u.id === idea.authorId);
		if (author) {
			author.totalVotesReceived++;
			author.reputation += 1;
		}
	}

	save(STORAGE_KEYS.ideas, store.ideas);
	save(STORAGE_KEYS.users, store.users);
	return idea;
}

export function addComment(
	ideaId: string,
	authorId: string,
	content: string,
): Comment {
	const comment: Comment = {
		id: "c_" + generateId(),
		ideaId,
		authorId,
		content,
		createdAt: new Date().toISOString(),
	};
	store.comments.push(comment);
	const idea = store.ideas.find((i) => i.id === ideaId);
	if (idea) idea.commentCount++;
	save(STORAGE_KEYS.comments, store.comments);
	save(STORAGE_KEYS.ideas, store.ideas);

	const user = store.users.find((u) => u.id === authorId);
	if (user) {
		user.reputation += 1;
		save(STORAGE_KEYS.users, store.users);
	}

	return comment;
}

export function getWeeklyRankedIdeas(): Idea[] {
	const oneWeekAgo = new Date();
	oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);

	return store.ideas
		.filter((i) => new Date(i.createdAt) >= oneWeekAgo)
		.sort((a, b) => b.votes - a.votes);
}

export function getTopCreators(): User[] {
	return [...store.users].sort((a, b) => b.reputation - a.reputation);
}

export function setCurrentUser(userId: string): User | null {
	const user = store.users.find((u) => u.id === userId);
	if (!user) return null;
	save(STORAGE_KEYS.currentUser, userId);
	return user;
}
